<template>
  <nuxt-link class="blog-tile" :class="rootClasses" :to="postUri">
    <u-i-image-handler
      class="blog-tile__image"
      img-format="avif"
      :img-loading="enableLazyLoad ? 'lazy' : 'eager'"
      img-densities="1x 2x"
      img-height="300px"
      :img-src="postImage?.mediaItemUrl"
      :img-alt="postImage?.altText" />
    <div class="blog-tile__blur-filter" v-if="tileStyle === 'slide'" />
    <div class="blog-tile__text-container">
      <div class="blog-tile__main-text">
        <ul v-if="tileStyle !== 'slide'" class="blog-tile__categories">
          <li
            class="blog-tile__category-item"
            v-for="category in postCategories"
            :key="category.uri">
            <nuxt-link class="blog-tile__category-link" :to="category.uri">
              {{ category.name }}
            </nuxt-link>
          </li>
        </ul>
        <div class="blog-tile__title">
          {{ postTitle }}
        </div>
      </div>
      <div class="blog-tile__meta-container">
        <div class="blog-tile__meta">
          {{ formattedDate }}<br />
          {{ postAuthor?.name }}
        </div>
        <nuxt-link class="blog-tile__read-more" :to="postUri">
          Read More
        </nuxt-link>
      </div>
    </div>
  </nuxt-link>
</template>

<script lang="ts">
export default {
  props: {
    tileStyle: String,
    postImage: Object,
    postUri: String,
    postTitle: String,
    postDate: String,
    postAuthor: Object,
    postCategories: Array,
    enableLazyLoad: Boolean,
  },
  computed: {
    rootClasses () {
      return {
        'blog-tile--large': this.tileStyle === 'large',
        'blog-tile--prominent': this.tileStyle === 'prominent',
        'blog-tile--compressed': this.tileStyle === 'compressed',
        'blog-tile--slide': this.tileStyle === 'slide',
      };
    },
    formattedDate () {
      const date = new Date(this.postDate);

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
  },
};
</script>

<style lang="scss">
@use "~/common/scss/helpers";
@use "~/common/scss/mq";
@use "~/common/scss/variables";

.blog-tile {
  $root: &;

  background-color: #000;
  border-radius: helpers.px-to-rem(10);
  color: #FFF;
  display: flex;
  flex-flow: column;
  height: 100%;
  overflow: hidden;
  position: relative;
  text-decoration: none;

  &--prominent {
    background-color: #FFFFFF;
    border: 1px solid #707070;
    color: #000000;

    @include mq.mq(variables.$mq-2-min) {
      flex-flow: row;
      height: helpers.px-to-rem(380);
    }
  }

  &--slide {
    height: helpers.px-to-rem(270);

    @include mq.mq(variables.$mq-2-min, variables.$mq-4-max) {
      height: helpers.px-to-rem(350);
    }
  }

  &__image {
    aspect-ratio: 16 / 9;
    height: auto;
    object-fit: cover;
    width: 100%;

    #{$root}--prominent & {
      width: auto;
    }

    #{$root}--slide & {
      aspect-ratio: unset;
      height: 100%;
      object-fit: cover;
    }
  }

  #{$root}--slide &__image,
  #{$root}--slide &__blur-filter {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  &__blur-filter {
    background-color: #00000033;
    backdrop-filter: blur(1rem);
    z-index: 2;
  }

  &__blur-filter,
  &__text-container,
  &__meta-container {

    #{$root}--slide & {
      opacity: 0;
      transition: opacity .25s;
      z-index: 10;
    }

    #{$root}--slide:hover & {
      opacity: 1;
    }
  }

  &__text-container {
    background: linear-gradient(to bottom, #00000000, #00000099 25%, #000000BB 75%, #000000FF);
    display: flex;
    flex-flow: column;
    height: 100%;
    justify-content: space-between;
    padding: helpers.px-to-rem(16) helpers.px-to-rem(20);

    #{$root}--large & {
      // otherwise Chrome displays a small non-black bar between title and meta text
      margin-bottom: -1px;
    }

    #{$root}--prominent & {
      background: transparent;
    }

    #{$root}--slide & {
      background: transparent;
    }
  }

  &__categories {
    display: flex;
    flex-flow: row wrap;
    gap: helpers.px-to-rem(8);
    list-style: none;
    margin-bottom: .25rem;
    padding: 0 0;
  }

  &__category-link {
    background-color: #3F90E0;
    border: 1px solid #3F90E0;
    border-radius: helpers.px-to-rem(15);
    color: #FFFFFF;
    display: inline-block;
    font-size: helpers.px-to-rem(12);
    text-decoration: none;
    transition: background-color .25s, color .25s;
    padding: helpers.px-to-rem(4) helpers.px-to-rem(12);

    &:hover {
      background-color: #FFFFFF;
      color: #3F90E0;
    }
  }

  &__title {
    font-size: helpers.px-to-rem(16);

    #{$root}--large & {
      font-size: helpers.px-to-rem(20);
    }

    #{$root}--prominent & {
      font-size: helpers.px-to-rem(18);
    }
  }

  &__meta-container {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-top: 1rem;
  }

  &__meta {
    color: #bebebe;
    font-size: helpers.px-to-rem(14);

    #{$root}--slide & {
      color: #FFF;
    }
  }

  &__read-more {
    align-items: center;
    background-color: #FFFFFF;
    border: 1px solid #FFFFFF;
    border-radius: helpers.px-to-rem(4);
    color: #000000;
    display: flex;
    font-size: helpers.px-to-rem(14);
    height: helpers.px-to-rem(40);
    justify-content: center;
    text-decoration: none;
    transition: background-color .25s, color .25s;
    padding: .25rem .75rem;
    width: helpers.px-to-rem(133);

    &:hover {
      background-color: #000000;
      color: #FFFFFF;
    }

    #{$root}--prominent & {
      border-color: #000000;
    }
  }
}
</style>
